import React, { useState, useEffect } from 'react';
import { Card, message, Modal, Switch, Tooltip, Button } from 'antd';
import { PrinterOutlined, ExclamationCircleOutlined, CheckCircleOutlined, DisconnectOutlined, DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import '../ScanPrint.css';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { getTemplate } from '../../utils/printerUtils';
import printStepsImage from '../../assets/print_steps.png';

// Import components
import ScanInput from './ScanPrint/ScanInput';
import PrintStats from './ScanPrint/PrintStats';
import PrintHistory from './ScanPrint/PrintHistory';
import LabelPreview from './ScanPrint/LabelPreview';
import OrderDetails from './ScanPrint/OrderDetails';
import PrintInstructions from './ScanPrint/PrintInstructions';

const ScanPrintContent = () => {
  const [loading, setLoading] = useState(false);
  const [usePluginPrint, setUsePluginPrint] = useState(true);
  const [backendOnline, setBackendOnline] = useState(false);
  const [successSound] = useState(new Audio('/assets/sounds/success.mp3'));
  const [errorSound] = useState(new Audio('/assets/sounds/error.mp3'));
  const [printedSound] = useState(new Audio('/assets/sounds/printed.mp3'));
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [printSpeed, setPrintSpeed] = useState(0);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [inputText, setInputText] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const textAreaRef = React.useRef(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [instructionModalVisible, setInstructionModalVisible] = useState(false);
  const [pluginGuideModalVisible, setPluginGuideModalVisible] = useState(false);
  // Cache for generated labels
  // Enhanced caching with expiration
  const cacheExpirationTime = 30 * 60 * 1000; // 30 minutes in milliseconds
  const labelCache = new Map();
  const cacheTimestamps = new Map(); // Track when items were added to cache
  
  // Clean expired cache items every 5 minutes
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      // Clean label cache
      for (const [key, timestamp] of cacheTimestamps.entries()) {
        if (now - timestamp > cacheExpirationTime) {
          labelCache.delete(key);
          cacheTimestamps.delete(key);
        }
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Check backend status with improved stability
  useEffect(() => {
    // Track consecutive failures to prevent flashing
    let consecutiveFailures = 0;
    const maxFailuresBeforeOffline = 2;
    
    const checkBackendStatus = async () => {
      const urls = ['http://127.0.0.1:8011/status', 'http://localhost:8011/status'];
      let success = false;
      
      for (const url of urls) {
        try {
          const response = await axios.get(url, { timeout: 2000 });
          if (response.status === 200) {
            consecutiveFailures = 0;
            setBackendOnline(true);
            success = true;
            break;
          }
        } catch (error) {
          // Continue to next URL
          console.log(`Failed to connect to ${url}`);
        }
      }
      
      if (!success) {
        consecutiveFailures++;
        console.log(`Backend check failure (${consecutiveFailures}/${maxFailuresBeforeOffline})`);
      }
      
      // Only set to offline after consecutive failures
      if (consecutiveFailures >= maxFailuresBeforeOffline) {
        setBackendOnline(false);
      }
    };
    
    // Check immediately
    checkBackendStatus();
    
    // Then check every 2 seconds (reduced frequency)
    const intervalId = setInterval(checkBackendStatus, 2000);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Load recent orders into session storage
  useEffect(() => {
    const loadRecentOrders = async () => {
      try {
        const response = await axios.get('/api/orders/recent/');
        console.log('Loaded orders:', response.data.length);
        
        // Create lookup maps for both order numbers and express numbers
        const orderNumberMap = {};
        const expressNumberMap = {};
        
        response.data.forEach(order => {
          // Add courier and port in uppercase
          const processedOrder = {
            ...order,
            courier: order.courier_service?.toUpperCase() || '',
            port: order.port?.toUpperCase() || ''
          };
          
          // Index by order number
          orderNumberMap[order.order_number] = processedOrder;
          
          // Index by express number if it exists
          if (order.express_number) {
            expressNumberMap[order.express_number] = processedOrder;
          }
        });

        // Store both maps in session storage
        sessionStorage.setItem('orderNumberMap', JSON.stringify(orderNumberMap));
        sessionStorage.setItem('expressNumberMap', JSON.stringify(expressNumberMap));
        console.log('Session storage updated with', Object.keys(orderNumberMap).length, 'orders');
      } catch (error) {
        console.error('Failed to load recent orders:', error);
      }
    };

    loadRecentOrders();

    // Preload sounds
    successSound.load();
    errorSound.load();
    printedSound.load();

    // Cleanup on unmount
    return () => {
      labelCache.clear();
    };
  }, [successSound, errorSound, printedSound]);

  const searchOrder = async (searchText) => {
    try {
      const trimmedSearch = searchText.trim();
      
      // Check both maps in session storage
      const orderNumberMap = JSON.parse(sessionStorage.getItem('orderNumberMap') || '{}');
      const expressNumberMap = JSON.parse(sessionStorage.getItem('expressNumberMap') || '{}');
      
      // Try to find in session storage first
      const order = expressNumberMap[trimmedSearch] || orderNumberMap[trimmedSearch];
      if (order) {
        console.log('Order found in session storage:', order.order_number);
        return [order];
      }

      console.log('Order not found in session storage, searching via API');
      
      // If not found in session storage, search via API
      const response = await axios.get('/api/orders/search/', {
        params: {
          search: trimmedSearch,
          type: 'express'
        }
      });

      if (response.data.length > 0) {
        const order = {
          ...response.data[0],
          courier: response.data[0].courier_service?.toUpperCase() || '',
          port: response.data[0].port?.toUpperCase() || ''
        };

        // Update session storage maps
        if (order.order_number) {
          orderNumberMap[order.order_number] = order;
          sessionStorage.setItem('orderNumberMap', JSON.stringify(orderNumberMap));
        }
        if (order.express_number) {
          expressNumberMap[order.express_number] = order;
          sessionStorage.setItem('expressNumberMap', JSON.stringify(expressNumberMap));
        }

        console.log('New order added to session storage:', order.order_number);
        return [order];
      }

      return [];
    } catch (error) {
      console.error('Search error:', error);
      return null;
    }
  };

  const generateLabel = async (order) => {
    // Check label cache first
    const cacheKey = `${order.id}-${order.print_times}`;
    if (labelCache.has(cacheKey)) {
      console.log('Using cached label for order:', order.order_number);
      return labelCache.get(cacheKey);
    }

    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.width = '100mm';
      tempDiv.style.height = '150mm';
      tempDiv.style.padding = '0';
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.boxSizing = 'border-box';

      let template;
      try {
        template = getTemplate(order.courier_service, order.port);
      } catch (error) {
        console.error('Template selection failed:', error);
        message.error(error.message);
        return null;
      }

      const orderWithCorrectAddress = {
        ...order,
        recipient_name: order.label_address ? order.label_recipient : order.recipient_name,
        recipient_phone: order.label_address ? order.label_phone : order.recipient_phone,
        recipient_address: order.label_address ? order.label_address : order.recipient_address
      };
      const labelHtml = await template(orderWithCorrectAddress);
      if (!labelHtml) {
        message.error('面单生成失败');
        return null;
      }

      tempDiv.innerHTML = labelHtml;
      document.body.appendChild(tempDiv);

      // Initial shorter delay with retry mechanism
      const maxAttempts = 5;
      const initialDelay = 50; // Start with 50ms
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, attempt === 1 ? initialDelay : initialDelay * 2));
        
        try {
          // Check if all images are loaded
          const images = tempDiv.getElementsByTagName('img');
          const allImagesLoaded = Array.from(images).every(img => img.complete);
          
          // Check if all fonts are loaded
          const fontsLoaded = await document.fonts.ready;
          
          if (!allImagesLoaded || !fontsLoaded) {
            if (attempt === maxAttempts) {
              console.warn('Max attempts reached, proceeding with render');
            } else {
              continue;
            }
          }
          
          break;
        } catch (error) {
          if (attempt === maxAttempts) {
            console.warn('Max attempts reached, proceeding with render');
          }
        }
      }

      const canvas = await html2canvas(tempDiv, {
        scale: 4,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 378,
        height: 567,
        onclone: (clonedDoc) => {
          const clonedDiv = clonedDoc.querySelector('div');
          if (clonedDiv) {
            clonedDiv.style.width = '378px';
            clonedDiv.style.height = '567px';
          }
        }
      });

      document.body.removeChild(tempDiv);

      // Cache the generated canvas with timestamp
      labelCache.set(cacheKey, canvas);
      cacheTimestamps.set(cacheKey, Date.now());
      return canvas;
    } catch (error) {
      console.error('Print error:', error);
      message.error('面单生成失败');
      return null;
    }
  };

  const calculatePrintSpeed = () => {
    if (!sessionStartTime) return;
    const successfulPrints = sessionHistory.filter(item => item.type === 'success' && item.printed).length;
    const sessionDuration = (new Date() - sessionStartTime) / 1000 / 60;
    const speed = sessionDuration > 0 ? successfulPrints / sessionDuration : 0;
    setPrintSpeed(speed);
  };

  useEffect(() => {
    calculatePrintSpeed();
  }, [sessionHistory]);

  const getSpeedInfo = (speed) => {
    if (speed >= 7) return {
      color: '#52c41a',
      message: '太厉害了！继续保持！'
    };
    if (speed >= 3) return {
      color: '#faad14',
      message: '你做的很棒，加油！'
    };
    return {
      color: '#f5222d',
      message: '稍微慢了点哦，加把劲！'
    };
  };

  const formatDuration = () => {
    if (!sessionStartTime) return '0:00';
    const duration = Math.floor((new Date() - sessionStartTime) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      calculatePrintSpeed();
    }, 1000);
    return () => clearInterval(timer);
  }, [sessionHistory]);

  const handlePrint = async (order) => {
    if (order.print_times > 0) {
      printedSound.play();
      Modal.confirm({
        title: '重复打印确认',
        icon: <ExclamationCircleOutlined />,
        content: `该订单已经打印过${order.print_times}次，是否确认再次打印？`,
        okText: '确认打印',
        cancelText: '取消',
        onOk: async () => {
          await performPrint(order);
        }
      });
    } else {
      await performPrint(order);
    }
  };

  // Optimized plugin print with retry logic
  const performPluginPrint = async (order, dataUrl) => {
    try {
      // Send order parameters to backend at port 8011
      console.log('Using plugin print for order:', order.order_number);
      
      // Prepare order data to send to plugin
      const orderData = {
        id: order.id,
        order_number: order.order_number,
        express_number: order.express_number,
        courier_service: order.courier_service,
        port: order.port,
        recipient_name: order.label_address ? order.label_recipient : order.recipient_name,
        recipient_phone: order.label_address ? order.label_phone : order.recipient_phone,
        recipient_address: order.label_address ? order.label_address : order.recipient_address,
        print_times: order.print_times,
        // Include the image data
        image_data: dataUrl
      };
      
      // Log the data being sent to the plugin (excluding image data for brevity)
      const logData = { ...orderData };
      delete logData.image_data;
      console.log('Sending data to print plugin:', logData);
      
      // Modified retry logic to prevent duplicate prints
      const maxRetries = 3;
      let retryCount = 0;
      let lastError = null;
      let printSuccess = false;
      
      // Add a unique request ID to track this specific print request
      const requestId = `${order.id}-${Date.now()}`;
      orderData.request_id = requestId;
      
      const printUrls = ['http://127.0.0.1:8011/print', 'http://localhost:8011/print'];
      
      while (retryCount < maxRetries && !printSuccess) {
        for (const printUrl of printUrls) {
          try {
            const response = await axios.post(printUrl, orderData, {
              timeout: 5000 // 5 second timeout
            });
            console.log('Plugin print response:', response.data);
            
            // Check if the response indicates success
            if (response.data && response.data.success) {
              printSuccess = true;
              return true;
            } else {
              // If the server responded but indicated failure
              throw new Error('Print server returned failure');
            }
          } catch (error) {
            lastError = error;
            console.log(`Failed to print to ${printUrl}:`, error.message);
            // Continue to next URL
          }
        }
        
        retryCount++;
        if (retryCount < maxRetries && !printSuccess) {
          // Exponential backoff: 500ms, 1000ms, 2000ms, etc.
          const backoffTime = Math.min(500 * Math.pow(2, retryCount - 1), 4000);
          console.log(`Print attempt ${retryCount} failed, retrying in ${backoffTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, backoffTime));
        }
      }
      
      // All retries failed
      console.error('Plugin print error after retries:', lastError);
      message.error('插件打印失败，请检查插件是否运行');
      return false;
    } catch (error) {
      console.error('Plugin print preparation error:', error);
      message.error('插件打印准备失败');
      return false;
    }
  };

  const performDirectPrint = async (order, dataUrl) => {
    try {
      console.log('Using direct print for order:', order.order_number);
      
      // Create and set up print iframe
      const iframe = document.createElement('iframe');
      Object.assign(iframe.style, {
        position: 'fixed',
        right: '0',
        bottom: '0',
        width: '0',
        height: '0',
        border: '0'
      });
      
      document.body.appendChild(iframe);
      
      // Write print content
      const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            @page { size: 100mm 150mm; margin: 0; }
            body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; }
            img { width: 98mm; height: 148mm; display: block; }
          </style>
          <script>
            function checkAndPrint() {
              const img = document.querySelector('img');
              const maxAttempts = 5;
              let attempt = 1;
              
              function tryPrint() {
                if (img.complete && img.naturalWidth > 0) {
                  window.print();
                } else if (attempt < maxAttempts) {
                  attempt++;
                  setTimeout(tryPrint, 50);
                } else {
                  console.warn('Max attempts reached, forcing print');
                  window.print();
                }
              }
              
              tryPrint();
            }
          </script>
        </head>
        <body>
          <img src="${dataUrl}" onload="checkAndPrint()" onerror="console.warn('Image load error'); window.print();" />
        </body>
      </html>
      `;
      iframe.contentDocument.write(printContent);
      iframe.contentDocument.close();
      
      // Wait for print dialog to close
      await new Promise(resolve => {
        const checkPrintDialogClosed = setInterval(() => {
          if (!iframe.contentWindow.document.hasFocus()) {
            clearInterval(checkPrintDialogClosed);
            document.body.removeChild(iframe);
            resolve();
          }
        }, 500);
      });
      
      return true;
    } catch (error) {
      console.error('Direct print error:', error);
      message.error('直接打印失败');
      return false;
    }
  };

const performPrint = async (order, providedDataUrl = null) => {
  try {
    // Use provided dataUrl if available, otherwise generate label
    let dataUrl = providedDataUrl;
    if (!dataUrl) {
      const canvas = await generateLabel(order);
      if (!canvas) return false;
      dataUrl = canvas.toDataURL('image/jpeg');
    }
      
      // Choose print method based on switch state
      let printSuccess = false;
      if (usePluginPrint) {
        printSuccess = await performPluginPrint(order, dataUrl);
      } else {
        printSuccess = await performDirectPrint(order, dataUrl);
      }
      
      // If printing failed, exit early
      if (!printSuccess) return;

      // Update session storage with incremented print_times
      const orderNumberMap = JSON.parse(sessionStorage.getItem('orderNumberMap') || '{}');
      const expressNumberMap = JSON.parse(sessionStorage.getItem('expressNumberMap') || '{}');
      
      const updatedOrder = {
        ...order,
        print_times: order.print_times + 1,
        status: 'PRINTED'
      };
      
      if (updatedOrder.order_number) {
        orderNumberMap[updatedOrder.order_number] = updatedOrder;
        sessionStorage.setItem('orderNumberMap', JSON.stringify(orderNumberMap));
      }
      if (updatedOrder.express_number) {
        expressNumberMap[updatedOrder.express_number] = updatedOrder;
        sessionStorage.setItem('expressNumberMap', JSON.stringify(expressNumberMap));
      }

      // Update UI immediately
      setSessionHistory(prev => [{
        type: 'success',
        timestamp: new Date().toLocaleTimeString(),
        searchValue: order.order_number,
        order: updatedOrder,
        printed: true
      }, ...prev]);

      // Update preview with the same label
      setPreviewUrl(dataUrl);

      // Start non-critical operations asynchronously
      Promise.all([
        axios.post('/api/orders/batch_print/', {
          order_ids: [order.id]
        }),
        axios.post(`/api/orders/${order.id}/history/`, {
          action: 'print'
        })
      ]).catch(error => {
        console.error('Failed to record print operation:', error);
      });
      
      return true;
    } catch (error) {
      console.error('Print error:', error);
      message.error({ content: '打印失败', key: 'printLoading' });
      return false;
    }
  };

  const updatePreview = async (order) => {
    // Check if we have a cached label first
    const cacheKey = `${order.id}-${order.print_times}`;
    if (labelCache.has(cacheKey)) {
      const canvas = labelCache.get(cacheKey);
      setPreviewUrl(canvas.toDataURL('image/jpeg'));
      return;
    }

    // If no cached label, generate a new one
    try {
      const canvas = await generateLabel(order);
      if (canvas) {
        setPreviewUrl(canvas.toDataURL('image/jpeg'));
      }
    } catch (error) {
      console.error('Preview generation failed:', error);
    }
  };

  const handleSearch = async (scanValue) => {
    setLoading(true);
    try {
      const searchResult = await searchOrder(scanValue);
      const timestamp = new Date().toLocaleTimeString();

      if (searchResult && searchResult.length > 0) {
        const order = searchResult[0];
        
        // Update session time if needed
        if (!sessionStartTime) {
          setSessionStartTime(new Date());
        }

        // Play success sound
        successSound.pause();
        errorSound.pause();
        successSound.currentTime = 0;
        successSound.play();

        // Start print process
        handlePrint(order);
      } else {
        // Handle error case
        setPreviewUrl(null);
        setSessionHistory(prev => [{
          type: 'error',
          timestamp,
          searchValue: scanValue
        }, ...prev]);
        
        successSound.pause();
        errorSound.pause();
        errorSound.currentTime = 0;
        errorSound.play();
        
        message.warning(`未找到订单: ${scanValue}`);
      }
    } catch (error) {
      message.error('搜索过程中出现错误');
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Optimized batch printing with request batching
  const handleBatchPrint = async () => {
    if (!batchInput.trim()) {
      message.warning('请输入订单号或快递单号');
      return;
    }
    setBatchLoading(true);
    try {
      const numbers = batchInput.split('\n').filter(line => line.trim());
      // Remove duplicates from input
      const uniqueNumbers = [...new Set(numbers.map(n => n.trim()))];
      const orders = [];
      const processedOrderIds = new Set(); // Track processed order IDs
      
      // First gather all orders
      for (const number of uniqueNumbers) {
        const searchResult = await searchOrder(number.trim());
        if (searchResult && searchResult.length > 0) {
          const order = searchResult[0];
          // Only add if we haven't processed this order ID yet
          if (!processedOrderIds.has(order.id)) {
            orders.push(order);
            processedOrderIds.add(order.id);
          }
        }
      }

      if (orders.length > 0) {
        // Batch process orders in groups of 3 for better performance
        const batchSize = 3;
        const batches = [];
        
        for (let i = 0; i < orders.length; i += batchSize) {
          batches.push(orders.slice(i, i + batchSize));
        }
        
        let successCount = 0;
        
        // Process each batch
        for (const batch of batches) {
          // Generate all canvases in parallel
          const canvasPromises = batch.map(order => generateLabel(order));
          const canvases = await Promise.all(canvasPromises);
          
          // Process each canvas in the batch
          for (let i = 0; i < batch.length; i++) {
            if (canvases[i]) {
              const dataUrl = canvases[i].toDataURL('image/jpeg');
              const success = await performPrint(batch[i], dataUrl);
              if (success) successCount++;
            }
          }
        }
        
        message.success(`成功打印 ${successCount} 个订单`);
        setBatchInput('');
      } else {
        message.warning('未找到任何有效订单');
      }
    } catch (error) {
      console.error('Batch print error:', error);
      message.error('批量打印过程中出现错误');
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <div style={{ padding: '12px', margin: '0 auto' }}>
      <div style={{ position: 'relative', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Switch 
            checked={usePluginPrint} 
            onChange={setUsePluginPrint} 
            style={{ marginRight: '8px' }}
          />
          <span style={{ marginRight: '8px' }}>{usePluginPrint ? '插件打印' : '无插件打印'}</span>
          {usePluginPrint && (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {backendOnline ? (
                <>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                  <span style={{ color: '#52c41a', marginRight: '8px' }}>插件在线</span>
                </>
              ) : (
                <>
                  <DisconnectOutlined style={{ color: '#f5222d', marginRight: '4px' }} />
                  <span style={{ color: '#f5222d', marginRight: '8px' }}>插件离线</span>
                </>
              )}
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const downloadLink = document.createElement('a');
                    downloadLink.href = '/assets/print.zip';
                    downloadLink.download = 'print.zip';
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                    message.success('插件下载成功');
                  }}
                >
                  下载插件
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setPluginGuideModalVisible(true)}
                  style={{ marginLeft: '4px' }}
                >
                  使用指南
                </Button>
              </span>
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '24px' }}>
        <Card title="扫描打印" extra={<PrinterOutlined />} style={{ flex: 1.1, width: 0 }}>
          <ScanInput
            inputText={inputText}
            setInputText={setInputText}
            batchInput={batchInput}
            setBatchInput={setBatchInput}
            handleSearch={handleSearch}
            handleBatchPrint={handleBatchPrint}
            batchLoading={batchLoading}
            setInstructionModalVisible={setInstructionModalVisible}
            textAreaRef={textAreaRef}
            usePluginPrint={usePluginPrint}
          />

          <PrintStats
            sessionStartTime={sessionStartTime}
            printSpeed={printSpeed}
            formatDuration={formatDuration}
            getSpeedInfo={getSpeedInfo}
            sessionHistory={sessionHistory}
          />

          <PrintHistory
            sessionHistory={sessionHistory}
            handlePrint={handlePrint}
            setSelectedOrder={setSelectedOrder}
            updatePreview={updatePreview}
          />
        </Card>

        <Card style={{ flex: 0.9, width: 0 }}>
          <LabelPreview previewUrl={previewUrl} />
        </Card>
      </div>

      <OrderDetails
        selectedOrder={selectedOrder}
        setSelectedOrder={setSelectedOrder}
        previewUrl={previewUrl}
      />

      <PrintInstructions
        visible={instructionModalVisible}
        setVisible={setInstructionModalVisible}
        printStepsImage={printStepsImage}
      />

      <Modal
        title="插件使用指南"
        open={pluginGuideModalVisible}
        onCancel={() => setPluginGuideModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPluginGuideModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        <div style={{ padding: '10px 0' }}>
          <h3>如何安装和使用打印插件</h3>
          <ol style={{ paddingLeft: '20px' }}>
            <li>下载插件压缩包并解压到您的电脑上</li>
            <li>运行解压后的可执行文件 (.exe)</li>
            <li>重启浏览器</li>
            <li>当插件状态显示"插件在线"时，即可开始使用</li>
          </ol>
          <div style={{ marginTop: '15px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            <p style={{ margin: '0', fontWeight: 'bold' }}>注意事项：</p>
            <ul style={{ paddingLeft: '20px', marginTop: '5px' }}>
              <li>插件需要保持运行状态才能使用打印功能</li>
              <li>如果遇到问题，请尝试重启插件和浏览器</li>
              <li>确保您的打印机已正确连接并设置为默认打印机</li>
            </ul>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default ScanPrintContent;
